import { db } from './index';
import { supabase } from '../lib/supabase';
import type { BackupData, SKU } from '../types';
import { toast } from 'react-hot-toast';

export async function createBackup(): Promise<BackupData> {
  try {
    const [skus, logs, settings] = await Promise.all([
      db.skus.toArray(),
      db.logs.toArray(),
      db.settings.get('default'),
    ]);

    if (!settings) {
      throw new Error('설정 데이터를 찾을 수 없습니다');
    }

    const backup: BackupData = {
      timestamp: new Date(),
      version: '1.0.0',
      skus,
      logs,
      settings,
    };

    return backup;
  } catch (error: any) {
    console.error('백업 생성 실패:', error.message);
    throw new Error(`백업 생성에 실패했습니다: ${error.message}`);
  }
}

export async function restoreBackup(backup: BackupData): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('로그인이 필요합니다');
    }

    // 버전 호환성 체크
    if (backup.version !== '1.0.0') {
      throw new Error(`지원되지 않는 백업 버전입니다: ${backup.version}`);
    }

    if (navigator.onLine) {
      // Supabase 데이터 삭제
      const { error: deleteError } = await supabase
        .from('skus')
        .delete()
        .or(`user_id.eq.${session.user.id},user_id.is.null`);

      if (deleteError) {
        throw new Error(`Supabase 데이터 삭제 실패: ${deleteError.message}`);
      }

      // Supabase에 백업 데이터 업로드 (배치 처리)
      if (backup.skus.length > 0) {
        const batchSize = 50;
        const batches = [];
        for (let i = 0; i < backup.skus.length; i += batchSize) {
          batches.push(backup.skus.slice(i, i + batchSize));
        }

        for (const batch of batches) {
          const { error: insertError } = await supabase.from('skus').upsert(
            batch.map((sku: SKU) => ({
              id: sku.id,
              style_no: sku.styleNo,
              color: sku.color,
              size: sku.size,
              expected_quantity: sku.expectedQuantity,
              actual_quantity: sku.actualQuantity,
              status: sku.status,
              progress: sku.progress,
              last_scanned: sku.lastScanned ? sku.lastScanned.toISOString() : null,
              last_modified: new Date().toISOString(),
              user_id: session.user.id,
            }))
          );

          if (insertError) {
            throw new Error(`Supabase 데이터 업로드 실패: ${insertError.message}`);
          }
        }
      }
    } else {
      toast.warn('오프라인 상태: 로컬 데이터만 복원됩니다.');
    }

    // 로컬 DB 복원
    await db.transaction('rw', [db.skus, db.logs, db.settings], async () => {
      await Promise.all([db.skus.clear(), db.logs.clear(), db.settings.clear()]);

      await Promise.all([
        db.skus.bulkAdd(backup.skus),
        db.logs.bulkAdd(backup.logs),
        db.settings.put(backup.settings),
      ]);
    });
  } catch (error: any) {
    console.error('복원 실패:', error.message);
    throw new Error(`데이터 복원에 실패했습니다: ${error.message}`);
  }
}

export function downloadBackup(backup: BackupData) {
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

  a.href = url;
  a.download = `kwms-backup-${timestamp}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function uploadBackup(file: File): Promise<void> {
  try {
    const text = await file.text();
    const backup = JSON.parse(text) as BackupData;

    // 백업 데이터 유효성 검사
    if (
      !backup.version ||
      !backup.timestamp ||
      !Array.isArray(backup.skus) ||
      !Array.isArray(backup.logs) ||
      !backup.settings
    ) {
      throw new Error('잘못된 백업 파일 형식입니다');
    }

    // SKU 데이터 유효성 검사
    for (const sku of backup.skus) {
      if (
        !sku.id ||
        !sku.styleNo ||
        !sku.color ||
        !sku.size ||
        typeof sku.expectedQuantity !== 'number' ||
        typeof sku.actualQuantity !== 'number'
      ) {
        throw new Error('백업 파일의 SKU 데이터가 올바르지 않습니다');
      }
      // Date 객체로 변환
      sku.lastScanned = sku.lastScanned ? new Date(sku.lastScanned) : null;
      sku.lastModified = sku.lastModified ? new Date(sku.lastModified) : new Date();
    }

    // Log 데이터 유효성 검사
    for (const log of backup.logs) {
      if (!log.id || !log.timestamp || !log.type || !log.message) {
        throw new Error('백업 파일의 로그 데이터가 올바르지 않습니다');
      }
      log.timestamp = new Date(log.timestamp);
    }

    // Settings 데이터 유효성 검사
    if (
      !backup.settings.id ||
      typeof backup.settings.autoSaveInterval !== 'number' ||
      typeof backup.settings.skuBatchSize !== 'number'
    ) {
      throw new Error('백업 파일의 설정 데이터가 올바르지 않습니다');
    }

    await restoreBackup(backup);
  } catch (error: any) {
    console.error('백업 파일 처리 실패:', error.message);
    throw new Error(`백업 파일을 처리할 수 없습니다: ${error.message}`);
  }
}